import React, {ChangeEvent, useEffect, useRef} from "react";
import {
    Box,
    Button,
    chakra,
    Checkbox,
    Flex,
    HStack,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Table,
    TableContainer,
    Tbody,
    Td,
    Th,
    Thead,
    Tr,
    useDisclosure,
} from "@chakra-ui/react";
import {
    useTable,
    useGlobalFilter,
    useSortBy,
    Column,
    usePagination,
    useRowSelect,
    Row,
} from "react-table";
import {FiArrowUp, FiArrowDown} from "react-icons/fi";
import SearchBox from "../search-box";
import Pagination from "../pagination";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import {BREAKPOINT_MD} from "@utils/constants/breakpoints";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {deletePrintJobs, createPrintJob} from "@features/print/action";
import {useDispatch} from "react-redux";
import {AppDispatch} from "@store/index";
import i18n from "i18next";

interface TableProps {
    columns: Array<Column<any>>;
    data: Array<any>;
    onSelectedRows: (rows: any) => void;
    selectedRows?: Row<any>;
    managePrintsRedirectUrl: string;
    newPrintButton?: boolean;
    showManageDelete?: boolean;
}

const IndeterminateCheckbox = ({showSelectLabel, ...rest}: any) => {
    const {t} = useTranslation();
    return (
        <>
            <Checkbox
                isIndeterminate={rest.indeterminate}
                isChecked={rest.checked}
                onChange={rest.onChange}
            >
                {
                    showSelectLabel &&
                    rest.checked ? t("main.deSelectAll") : showSelectLabel &&
                    !rest.checked ? t("main.selectAll") : null
                }
            </Checkbox>
        </>
    );
};

const selectionPlugin = (hooks: any) => {
    hooks.visibleColumns.push((columns: any[]) => [
        {
            id: "selection",
            Header: ({getToggleAllRowsSelectedProps}: any) => (
                <HStack spacing={'12px'}>
                    <IndeterminateCheckbox showSelectLabel={true} {...getToggleAllRowsSelectedProps()} />
                </HStack>
            ),
            Cell: ({row}: any) => (
                <Box>
                    <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                </Box>
            ),
        },
        ...columns,
    ]);
};

const Index = ({
                   onSelectedRows,
                   managePrintsRedirectUrl,
                   newPrintButton,
                   showManageDelete,
                   data,
                   columns,
               }: TableProps) => {
    const {width} = useWindowDimensions();
    const {t} = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const {isOpen, onOpen, onClose} = useDisclosure();

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        gotoPage,
        pageCount,
        setPageSize,
        selectedFlatRows,
        toggleAllRowsSelected,
        state: {pageIndex, pageSize, selectedRowIds, globalFilter},
        setGlobalFilter,
    } = useTable(
        {
            columns,
            data,
            disableSortBy: width < BREAKPOINT_MD,
            initialState: {
                pageSize: 20,
                hiddenColumns: columns
                    .filter((col: any) => col.show === false)
                    .map((col) => col.id || col.accessor) as any,
            },
            autoResetSelectedRows: false,
            autoResetSelectedCell: false,
            autoResetSelectedColumn: false,
            autoResetHiddenColumns: false,
            autoResetPage: false,
            autoResetSortBy: false,
            autoResetGlobalFilter: false,
            autoResetFilters: false,
        },
        useGlobalFilter,
        useSortBy,
        usePagination,
        useRowSelect,
        selectionPlugin
    );

    const lang = i18n.language;

    const onSelectedRowsRef = useRef(onSelectedRows);
    onSelectedRowsRef.current = onSelectedRows;

    useEffect(() => {
        onSelectedRowsRef.current(selectedFlatRows);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFlatRows]);

    const isFirstIndex = (x: number) => {
        return x === 0 ? "0" : "var(--chakra-space-4) var(--chakra-space-6)";
    };

    const handlePageSizeChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setPageSize(Number(e.target.value));
    };

    const selected = selectedFlatRows.map((d: any) => d.original);

    const handleReprint = () => {
        const reprints = selectedFlatRows.map((d: any) => {
            const { ID, TIMESTAMP, TOTAL_PRICE, ACCESS_ID, PAGES, PRINTER_ID, RESERVATION_ID, ...rest } = d.original;
            return { ...rest, STATUS: 11 };
        });

        dispatch(createPrintJob({ body: reprints }))
            .then(() => navigate(`/${lang}/${t("router.mijn-wachtrij")}`));
    };

    return (
        <>
            <SearchBox filter={globalFilter} setFilter={setGlobalFilter}/>

            <Flex mb={"45px"} justifyContent={newPrintButton ? 'space-between' : 'flex-end'} flexWrap={"wrap"} gap={"12px"}>
                { newPrintButton && <Button
                    variant={"primary"}
                    onClick={() => {
                       navigate(`/${lang}${t("pageRoutes.nieuwe-prints")}`);
                    }}
                >
                    {t("table.topbar.addPrint")}
                </Button>}

                <Flex flexWrap={"wrap"} gap={"12px"} sx={{ "& > *": { flexShrink: 0 } }}>
                    {data.find((item) => item.STATUS === 3) && (
                        <Button
                            variant={"outline"}
                            isDisabled={
                                Object.getOwnPropertyNames(selectedRowIds).length === 0
                            }
                            onClick={handleReprint}
                        >
                            {t("table.topbar.reprint")}
                        </Button>
                    )}

                    {showManageDelete !== false && Object.getOwnPropertyNames(selectedRowIds).length > 1 && (
                        <Button
                            variant={"outline"}
                            isDisabled={Object.getOwnPropertyNames(selectedRowIds).length < 1}
                            onClick={() => {
                                navigate(`/${lang}${t("pageRoutes.beheer-print")}`, {
                                    state: {
                                        selected: selected,
                                        managePrintsRedirectUrl: managePrintsRedirectUrl,
                                    },
                                });
                            }}
                        >
                            {t("table.topbar.managePrints", {
                                amount: Object.getOwnPropertyNames(selectedRowIds).length,
                            })}
                        </Button>
                    )}

                    {showManageDelete !== false && Object.getOwnPropertyNames(selectedRowIds).length > 1 && (
                        <>
                            <Button
                                variant={"outline"}
                                isDisabled={
                                    Object.getOwnPropertyNames(selectedRowIds).length < 1
                                }
                                onClick={onOpen}
                            >
                                {t("table.topbar.deletePrints.text", {
                                    amount: Object.getOwnPropertyNames(selectedRowIds).length,
                                })}
                            </Button>

                            <Modal isOpen={isOpen} onClose={onClose} isCentered>
                                <ModalOverlay/>
                                <ModalContent>
                                    <ModalHeader>
                                        {t("table.topbar.deletePrints.modal.title")}
                                    </ModalHeader>
                                    <ModalCloseButton/>

                                    <ModalBody>
                                        {t("table.topbar.deletePrints.modal.description")}
                                    </ModalBody>

                                    <ModalFooter>
                                        <Button mr={3} onClick={onClose}>
                                            {t("table.topbar.deletePrints.modal.cancelBtn")}
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={() => {
                                                onClose();
                                                dispatch(deletePrintJobs({
                                                    ids: selectedFlatRows.map(
                                                        (d: any) => d.original.ID
                                                    ),
                                                }));
                                                toggleAllRowsSelected(false);
                                            }}
                                        >
                                            {t("table.topbar.deletePrints.modal.confirmBtn")}
                                        </Button>
                                    </ModalFooter>
                                </ModalContent>
                            </Modal>
                        </>
                    )}

                    {showManageDelete !== false && Object.getOwnPropertyNames(selectedRowIds).length === 1 && (
                        <Button
                            variant={"outline"}
                            isDisabled={
                                Object.getOwnPropertyNames(selectedRowIds).length === 0
                            }
                            onClick={() => {
                                navigate(`/${lang}${t("pageRoutes.beheer-print")}`, {
                                    state: {
                                        selected: selected,
                                        managePrintsRedirectUrl: managePrintsRedirectUrl,
                                    },
                                });
                            }}
                        >
                            {t("table.topbar.managePrint")}
                        </Button>
                    )}

                    {showManageDelete !== false && Object.getOwnPropertyNames(selectedRowIds).length === 1 && (
                        <>
                            <Button
                                variant={"outline"}
                                isDisabled={
                                    Object.getOwnPropertyNames(selectedRowIds).length === 0
                                }
                                onClick={onOpen}
                            >
                                {t("table.topbar.deletePrint.text")}
                            </Button>

                            <Modal isOpen={isOpen} onClose={onClose} isCentered>
                                <ModalOverlay/>
                                <ModalContent>
                                    <ModalHeader>
                                        {t("table.topbar.deletePrints.modal.title")}
                                    </ModalHeader>
                                    <ModalCloseButton/>

                                    <ModalBody>
                                        {t("table.topbar.deletePrints.modal.description")}
                                    </ModalBody>

                                    <ModalFooter>
                                        <Button mr={3} onClick={onClose}>
                                            {t("table.topbar.deletePrints.modal.cancelBtn")}
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={() => {
                                                onClose();
                                                dispatch(deletePrintJobs({
                                                    ids: selectedFlatRows.map(
                                                        (d: any) => d.original.ID
                                                    ),
                                                }));
                                                toggleAllRowsSelected(false);
                                            }}
                                        >
                                            {t("table.topbar.deletePrints.modal.confirmBtn")}
                                        </Button>
                                    </ModalFooter>
                                </ModalContent>
                            </Modal>
                        </>
                    )}

                    <Select
                        variant={"black"}
                        width={"auto"}
                        onChange={handlePageSizeChange}
                    >
                        {[20, 50, 100].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                                {t("table.topbar.showItems", {amount: pageSize})}
                            </option>
                        ))}
                    </Select>
                </Flex>
            </Flex>
            <TableContainer mb={"45px"} p={"0 3px"}>
                <Table {...getTableProps()} mb={"15px"}>
                    <Thead>
                        {headerGroups.map((headerGroup) => {
                            const { key: hgKey, ...hgProps } = headerGroup.getHeaderGroupProps();
                            return (
                            <Tr key={hgKey} {...hgProps}>
                                {headerGroup.headers.map((column, i) => {
                                    const { key: colKey, ...colProps } = column.getHeaderProps(column.getSortByToggleProps());
                                    return (
                                    <Th
                                        key={colKey}
                                        {...colProps}
                                        padding={isFirstIndex(i)}
                                    >
                                        {i === 0 ? (
                                            column.render("Header")
                                        ) : (
                                            <Flex alignItems={"center"}>
                                                {column.render("Header")}
                                                <chakra.span pl={"4px"}>
                                                    {column.isSorted && width > BREAKPOINT_MD ? (
                                                        column.isSortedDesc ? (
                                                            <FiArrowDown aria-label="Sorted descending"/>
                                                        ) : (
                                                            <FiArrowUp aria-label="Sorted ascending"/>
                                                        )
                                                    ) : null}
                                                </chakra.span>
                                            </Flex>
                                        )}
                                    </Th>
                                    );
                                })}
                            </Tr>
                            );
                        })}
                    </Thead>
                    <Tbody {...getTableBodyProps()}>
                        {page.map((row) => {
                            prepareRow(row);
                            const { key: rowKey, ...rowProps } = row.getRowProps();
                            return (
                                <Tr key={rowKey} {...rowProps}>
                                    {row.cells.map((cell, i) => {
                                        const { key: cellKey, ...cellProps } = cell.getCellProps();
                                        return (
                                            <Td key={cellKey} {...cellProps} padding={isFirstIndex(i)}>
                                                {cell.render("Cell")}
                                            </Td>
                                        );
                                    })}
                                </Tr>
                            );
                        })}
                    </Tbody>
                </Table>
            </TableContainer>

            {pageCount > 1 ? (
                <Flex justifyContent={"center"}>
                    <Pagination
                        className="pagination-bar"
                        currentPage={pageIndex + 1}
                        totalCount={data.length}
                        pageSize={pageSize}
                        onPageChange={(page) => gotoPage(page - 1)}
                        siblingCount={2}
                    />
                </Flex>
            ) : null}
        </>
    );
};

export default Index;
